import React, { useContext } from "react";
import { AppBar, Toolbar, Typography, IconButton, Box, Avatar, Badge, Tooltip } from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import Brightness4Icon from '@mui/icons-material/Brightness4';
import Brightness7Icon from '@mui/icons-material/Brightness7';
import ExitToAppIcon from "@mui/icons-material/ExitToApp";
import SystemUpdateAltIcon from '@mui/icons-material/SystemUpdateAlt';
import { UserContext } from "../../context/UserContext";
import { useNavigate } from "react-router-dom";
import { signOut } from "firebase/auth";
import { auth } from "../../utils/firebase-config";
import { useTheme, useMediaQuery } from "@mui/material";
import { APP_BRANDING } from "../../config/app-branding";
import { useUpdateAvailable } from "../../hooks/useUpdateAvailable";

function TopBar({ handleMenuClick, handleThemeToggle, isDarkMode }) {
  const { currentUser } = useContext(UserContext);
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { updateAvailable, buildInfo, handleUpdate } = useUpdateAvailable();

  // Formatear timestamp para tooltip
  const formatTimestamp = (timestamp) => {
    if (!timestamp || timestamp.length !== 14) return timestamp;
    const year = timestamp.substring(0, 4);
    const month = timestamp.substring(4, 6);
    const day = timestamp.substring(6, 8);
    const hour = timestamp.substring(8, 10);
    const minute = timestamp.substring(10, 12);
    const second = timestamp.substring(12, 14);
    return `${year}-${month}-${day} ${hour}:${minute}:${second}`;
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
      navigate("/login");
    } catch (error) {
      console.error("Error al cerrar sesión: ", error);
    }
  };

  const getInitials = (name) => {
    return name
      ? name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase()
      : 'U';
  };

  return (
    <AppBar
      position="sticky"
      elevation={0}
      sx={{
        height: isMobile ? 60 : 64,
        justifyContent: "center",
      }}
    >
      <Toolbar>
        <IconButton
          color="inherit"
          edge="start"
          onClick={handleMenuClick}
          sx={{ mr: 2 }}
        >
          <MenuIcon />
        </IconButton>

        <Typography variant="h6" sx={{ flexGrow: 1, fontWeight: 800, letterSpacing: '1px', textTransform: 'uppercase' }}>
          {APP_BRANDING.name}
        </Typography>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {updateAvailable && (
            <Tooltip 
              title={
                <div>
                  Nueva versión disponible
                  {buildInfo && (
                    <div style={{ fontSize: '0.75rem', marginTop: '4px' }}>
                      v{buildInfo.version} - {formatTimestamp(buildInfo.timestamp)}
                    </div>
                  )}
                  <div style={{ fontSize: '0.75rem', marginTop: '4px', fontWeight: 'bold' }}>
                    Haz clic para actualizar
                  </div>
                </div>
              }
              arrow
            >
              <IconButton 
                color="inherit" 
                onClick={handleUpdate}
                sx={{
                  animation: 'pulse 2s ease-in-out infinite',
                  '@keyframes pulse': {
                    '0%, 100%': {
                      opacity: 1,
                    },
                    '50%': {
                      opacity: 0.7,
                    },
                  },
                }}
              >
                <Badge 
                  variant="dot" 
                  color="error"
                  overlap="circular"
                >
                  <SystemUpdateAltIcon />
                </Badge>
              </IconButton>
            </Tooltip>
          )}
          <IconButton color="inherit" onClick={handleThemeToggle}>
            {isDarkMode ? <Brightness7Icon /> : <Brightness4Icon />}
          </IconButton>
          
          {!isMobile && (
             <Box sx={{ display: 'flex', alignItems: 'center', ml: 2, gap: 1 }}>
                <Avatar 
                  sx={{ 
                    width: 32, 
                    height: 32, 
                    bgcolor: theme.palette.mode === 'light' ? '#000' : '#fff',
                    color: theme.palette.mode === 'light' ? '#fff' : '#000',
                    fontSize: 14,
                    fontWeight: 'bold'
                  }}
                >
                  {getInitials(currentUser?.displayName || currentUser?.email)}
                </Avatar>
             </Box>
          )}

          <IconButton color="inherit" onClick={handleLogout} sx={{ ml: 1 }}>
            <ExitToAppIcon />
          </IconButton>
        </Box>
      </Toolbar>
    </AppBar>
  );
}

export default TopBar;
